import { createContext, useContext, useMemo, useReducer } from "react";
import { buildFieldKey } from "../utils/buildFieldKey";

export const ACTIONS = Object.freeze({
    SET_REQUIREMENTS: "SET_REQUIREMENTS",
    SET_FILES: "SET_FILES",
    SET_FIELD_VALUES: "SET_FIELD_VALUES",
    SET_PEER_COMMENT: "SET_PEER_COMMENT",
    SET_SECTION_C_COUNT: "SET_SECTION_C_COUNT",
    SET_SECTION_B_MIN_CRITERIA: "SET_SECTION_B_MIN_CRITERIA",
});

const initialState = {
    requirements: {},
    fields: {},
    sectionCCount: 0,
    sectionBMinCriteria: 0,
};

const reducer = (state, action) => {
    switch (action.type) {
        case ACTIONS.SET_REQUIREMENTS: {
            const requirements = { ...state.requirements };
            action.items.forEach((item) => {
                requirements[item.requirementId] = {
                    ...requirements[item.requirementId],
                    selfEvalRequirementId: item.selfEvalRequirementId,
                    comment: item.comment,
                    caseOfficerComments: item.clarificationComments,
                    status: item.statusName,
                    peerReviewerComments: item.peerReviewerComment,
                };
            });
            return { ...state, requirements };
        }

        // replaces files (idempotent) -> no duplicate checks needed
        case ACTIONS.SET_FILES: {
            const grouped = {};
            action.items.forEach((item) => {
                if (!grouped[item.requirementId]) grouped[item.requirementId] = [];
                grouped[item.requirementId].push({
                    id: item.fileId,
                    name: item.selfEvaluationFileName,
                    url: item.selfEvaluationFilePath,
                });
            });
            const requirements = { ...state.requirements };
            Object.entries(grouped).forEach(([reqId, files]) => {
                requirements[reqId] = { ...requirements[reqId], files };
            });
            return { ...state, requirements };
        }

        case ACTIONS.SET_FIELD_VALUES: {
            const fields = {};
            action.items.forEach((item) => {
                fields[buildFieldKey(item.additionalFieldId, item.productDetailsId)] = {
                    value: item.additionalFieldValue,
                    comment: item.comments,
                };
            });
            return { ...state, fields };
        }

        case ACTIONS.SET_PEER_COMMENT:
            return {
                ...state,
                requirements: {
                    ...state.requirements,
                    [action.reqId]: {
                        ...state.requirements[action.reqId],
                        peerReviewerComments: action.value,
                    },
                },
            };

        case ACTIONS.SET_SECTION_C_COUNT:
            return state.sectionCCount === action.value
                ? state
                : { ...state, sectionCCount: action.value };

        case ACTIONS.SET_SECTION_B_MIN_CRITERIA:
            return state.sectionBMinCriteria === action.value
                ? state
                : { ...state, sectionBMinCriteria: action.value };

        default:
            return state;
    }
};

const SelfEvaluationInfoContext = createContext(null);

export const SelfEvaluationInfoProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const value = useMemo(() => ({ state, dispatch }), [state]);
    return <SelfEvaluationInfoContext.Provider value={value}>{children}</SelfEvaluationInfoContext.Provider>;
};

export const useSelfEvaluationInfoContext = () => {
    const ctx = useContext(SelfEvaluationInfoContext);
    if (!ctx) throw new Error("useSelfEvaluationInfoContextContext must be used inside <SelfEvaluationInfoProvider>");
    return ctx;
};